import {
  IBuildings, IBuildingsHasInteractiveVoiceResponses, ICallGroups, ICallGroupsHasInteractiveVoiceResponses, ICallTrackingNumbers,
} from '@llws/lift-entity-interfaces';
import { AI_VOICE_AGENT_MODULE } from '@/shared/constants/ivr';
import { TPremiumProductKey } from '@/shared/types/premium-products';

/**
 * Where a call tracking number sends its calls.
 * `ivr` and `forwarding` are the base routes, used when the number has no call group; the number's IVR flag picks between them.
 */
export type TRouteKind = 'ivr' | 'forwarding' | 'callGroup' | 'aiAgent';

/** The routes that ring a call group, leaving the number's forwarding settings as their fallback. */
export type TGroupRouteKind = Extract<TRouteKind, 'callGroup' | 'aiAgent'>;

/** A route chosen for a number. `callGroupId` is the group a `callGroup` route rings; other kinds derive their group. */
export interface IRouteSelection {
  kind: TRouteKind;
  callGroupId: number | null;
}

export interface INumberRoutingInput {
  number: Pick<ICallTrackingNumbers, 'buildingId' | 'callGroupId' | 'callGroupDialTimeout' | 'useIVR'>;
  /** Whether the number's property has an active IVR, so the number can be routed to it. The number's IVR flag says whether it is. */
  isIVREligible: boolean;
  /** The client's call groups, with their entries. */
  callGroups: ICallGroups[];
  /** The premium products of the number's property. */
  propertyProducts: TPremiumProductKey[];
}

/** A call group the number can be routed to, as the picker lists it. */
export interface IPickableCallGroup {
  id: number;
  name: string;
  entryCount: number;
  /** Whether the group has no enabled entries, so calls on it go straight to the fallback. */
  hasNoEnabledEntries: boolean;
  /** Whether the group is already used on the number's property, by an IVR route or another call tracking number. */
  usedOnProperty: boolean;
}

export interface INumberRouting {
  /** The routes to offer, in display order. Always includes the number's current route. */
  kinds: TRouteKind[];
  /** Whether the "Route calls to" choice appears. With a single route there is nothing to choose. */
  showSelector: boolean;
  /** The route the number uses as saved. */
  current: IRouteSelection;
  /** The groups a call group route can ring, in the client's order. Always includes the saved group when it is loaded. */
  pickableGroups: IPickableCallGroup[];
}

export type TRouteProblemCode = 'noProperty' | 'productMissing' | 'notVisible' | 'noGroupSelected' | 'noEnabledEntries';

/** The message shown for a problem: usually named after its code, with call group wording for an AI agent entry on a call group route. */
type TRouteProblemMessage = TRouteProblemCode | 'groupNoProperty' | 'groupProductMissing';

export interface IRouteProblem {
  code: TRouteProblemCode;
  blocksSave: boolean;
  /** Translation key of the message shown to the user. */
  messageKey: string;
}

/** The number fields a route choice writes. Nothing else on the number changes with the route; an absent field stays as saved. */
export type TRouteFields = Pick<ICallTrackingNumbers, 'callGroupId' | 'callGroupDialTimeout'> & Partial<Pick<ICallTrackingNumbers, 'useIVR' | 'callRecording'>>;

const ROUTE_KIND_ORDER: TRouteKind[] = ['ivr', 'forwarding', 'callGroup', 'aiAgent'];

/** Whether a route rings a call group (the AI leasing agent is one), leaving the forwarding settings as its fallback. */
export const isGroupRoute = (kind: TRouteKind): boolean => kind === 'callGroup' || kind === 'aiAgent';

/** Whether a call group rings the AI agent: it holds an entry flagged as one, enabled or not. */
export const hasAiAgentEntry = (group: Pick<ICallGroups, 'callGroupEntries'>): boolean => group.callGroupEntries?.some((entry) => entry.isAiAgent) ?? false;

const hasEnabledEntry = (group: ICallGroups): boolean => group.callGroupEntries?.some((entry) => entry.status === 'enabled') ?? false;

const hasId = (group: ICallGroups): group is ICallGroups & { id: number } => typeof group.id === 'number';

/**
 * Returns the client's AI Agent call group: the group holding an entry flagged as an AI agent, whether that entry is enabled or not.
 * Each client is expected to have a single one.
 */
export const findAiAgentCallGroup = (callGroups: ICallGroups[]): ICallGroups | undefined => callGroups.find(hasAiAgentEntry);

/** The property's own IVR (not a sub-menu IVR), switched on. */
export const isEnabledRootIVR = (bhivr: IBuildingsHasInteractiveVoiceResponses): boolean => (
  bhivr.interactiveVoiceResponse?.status === 'enabled' && !bhivr.interactiveVoiceResponse?.parentInteractiveVoiceResponseId
);

/**
 * Whether a property has an active IVR, so its call tracking numbers can be routed to it.
 * It needs the IVR product, IVR (or the IVR add-on) turned on, and an enabled IVR of its own.
 * @param building - The number's property, with its IVR links and their IVRs joined; undefined for a number without a property.
 * @param propertyProducts - The premium products of that property.
 */
export const isPropertyIVREligible = (
  building: Pick<IBuildings, 'useIVR' | 'useIVRAddon' | 'buildingsHasInteractiveVoiceResponses'> | undefined,
  propertyProducts: TPremiumProductKey[],
): boolean => {
  if (!building) return false;
  if (!propertyProducts.includes('interactive_voice_responses')) return false;
  if (![building.useIVR, building.useIVRAddon].includes(1)) return false;

  return building.buildingsHasInteractiveVoiceResponses?.some(isEnabledRootIVR) ?? false;
};

const getBaseKind = (input: INumberRoutingInput): TRouteKind => (input.isIVREligible && input.number.useIVR === 1 ? 'ivr' : 'forwarding');

const propertyHasAiAgent = (input: INumberRoutingInput): boolean => (
  typeof input.number.buildingId === 'number' && input.propertyProducts.includes(AI_VOICE_AGENT_MODULE)
);

/** Names the route of a number that has a call group: the AI leasing agent when it is the client's AI Agent group. */
const getGroupRouteKind = (callGroupId: number, aiAgentCallGroupId: number | null | undefined): TGroupRouteKind => (
  callGroupId === aiAgentCallGroupId ? 'aiAgent' : 'callGroup'
);

const getCurrentRoute = (input: INumberRoutingInput): IRouteSelection => {
  const { callGroupId } = input.number;
  if (typeof callGroupId !== 'number') return { kind: getBaseKind(input), callGroupId: null };

  return { kind: getGroupRouteKind(callGroupId, findAiAgentCallGroup(input.callGroups)?.id), callGroupId };
};

const routeProblem = (code: TRouteProblemCode, options: { blocksSave?: boolean, messageName?: TRouteProblemMessage } = {}): IRouteProblem => ({
  code,
  blocksSave: options.blocksSave ?? true,
  messageKey: `callTracking.single.routing.problems.${options.messageName ?? code}`,
});

/**
 * Checks a group holding an AI agent entry, which needs the number to have a property that has the AI leasing agent.
 * On a call group route the message says the group includes the agent, since its other entries still ring.
 */
const getAiAgentEntryProblem = (input: INumberRoutingInput, group: ICallGroups, kind: TRouteKind): IRouteProblem | null => {
  if (!hasAiAgentEntry(group)) return null;

  const onCallGroup = kind === 'callGroup';
  if (typeof input.number.buildingId !== 'number') return routeProblem('noProperty', { messageName: onCallGroup ? 'groupNoProperty' : undefined });
  if (!propertyHasAiAgent(input)) return routeProblem('productMissing', { messageName: onCallGroup ? 'groupProductMissing' : undefined });

  return null;
};

/** Whether an IVR route or a call tracking number on the building uses the group. Numbers pending release don't count. */
const isUsedOnBuilding = (group: ICallGroups, buildingId: number): boolean => {
  const usedByNumber = group.callTrackingNumbers?.some((number) => number.buildingId === buildingId && number.pendingDelete !== 1) ?? false;
  const usedByIVRRoute = group.callGroupsHasInteractiveVoiceResponses?.some((route) => (
    route.interactiveVoiceResponse?.buildingsHasInteractiveVoiceResponses?.some((link) => link.buildingId === buildingId)
  )) ?? false;

  return usedByNumber || usedByIVRRoute;
};

const toPickableCallGroup = (group: ICallGroups & { id: number }, buildingId: number | null | undefined): IPickableCallGroup => ({
  id: group.id,
  name: group.name,
  entryCount: group.callGroupEntries?.length ?? 0,
  hasNoEnabledEntries: !hasEnabledEntry(group),
  usedOnProperty: typeof buildingId === 'number' && isUsedOnBuilding(group, buildingId),
});

/**
 * Lists the groups a call group route can ring: the client's groups other than the AI Agent group, which is its own route,
 * and without groups ringing the AI agent unless the property has the AI leasing agent. Groups that ring nobody stay, flagged.
 * The saved group stays even when it no longer qualifies, so the picker shows where calls go.
 * Each group says whether the property already uses it, so the picker can list those first.
 */
const getPickableGroups = (input: INumberRoutingInput, current: IRouteSelection): IPickableCallGroup[] => {
  const aiAgentGroupId = findAiAgentCallGroup(input.callGroups)?.id;
  const isSavedGroup = (group: ICallGroups): boolean => current.kind === 'callGroup' && group.id === current.callGroupId;

  return input.callGroups
    .filter(hasId)
    .filter((group) => group.id !== aiAgentGroupId)
    .filter((group) => !getAiAgentEntryProblem(input, group, 'callGroup') || isSavedGroup(group))
    .map((group) => toPickableCallGroup(group, input.number.buildingId));
};

/**
 * Works out which routes a call tracking number can be given and which one it has now.
 * The forwarding number is always offered, and the property IVR when the property has an active IVR.
 * Call groups are offered when there is a group to pick. The AI leasing agent is offered when the number's property
 * has the product and the client has an AI Agent group.
 * A saved route stays on offer even when it no longer qualifies, so the page shows where calls really go.
 */
export const getNumberRouting = (input: INumberRoutingInput): INumberRouting => {
  const current = getCurrentRoute(input);
  const pickableGroups = getPickableGroups(input, current);
  const offered = new Set<TRouteKind>(['forwarding', current.kind]);

  if (input.isIVREligible) offered.add('ivr');
  if (pickableGroups.length) offered.add('callGroup');
  if (propertyHasAiAgent(input) && findAiAgentCallGroup(input.callGroups)) offered.add('aiAgent');

  const kinds = ROUTE_KIND_ORDER.filter((kind) => offered.has(kind));
  return {
    kinds, showSelector: kinds.length > 1, current, pickableGroups,
  };
};

/**
 * Returns the route selected when the user picks a kind.
 * Picking the saved kind again restores the saved group, so a routed number can be switched away and back without losing it.
 */
export const chooseRoute = (routing: INumberRouting, kind: TRouteKind): IRouteSelection => ({
  kind,
  callGroupId: kind === routing.current.kind ? routing.current.callGroupId : null,
});

/**
 * Returns the number fields to save for a route: the call group to ring (null for a base route), the number's IVR flag,
 * call recording turned on for the AI leasing agent, whose calls are always recorded,
 * and the ring time per group entry, which only a call group route keeps. Null rings each entry for the default 15 seconds.
 * The IVR flag is on only for the property IVR. The forwarding number leaves it alone on a property with no active IVR,
 * so an IVR set up there later still answers the number.
 */
export const getRouteFieldsToSave = (input: INumberRoutingInput, selection: IRouteSelection): TRouteFields => {
  if (selection.kind === 'aiAgent') {
    // The AI leasing agent is only offered when the client has an AI Agent group, so the group is always found here.
    return {
      callGroupId: findAiAgentCallGroup(input.callGroups)?.id ?? null, callGroupDialTimeout: null, useIVR: 0, callRecording: 1,
    };
  }

  if (selection.kind === 'callGroup') {
    return { callGroupId: selection.callGroupId, callGroupDialTimeout: input.number.callGroupDialTimeout ?? null, useIVR: 0 };
  }

  if (selection.kind === 'ivr') return { callGroupId: null, callGroupDialTimeout: null, useIVR: 1 };
  if (!input.isIVREligible) return { callGroupId: null, callGroupDialTimeout: null };

  return { callGroupId: null, callGroupDialTimeout: null, useIVR: 0 };
};

/**
 * Checks a route before it is saved, mirroring the rules the API enforces, because the production gateway hides the API's reasons.
 * A call group route needs a group picked. A group missing from the client's groups can't be checked, so saving it is refused
 * until the number is re-routed. A picked group that rings nobody is only a warning: calls then go to the fallback.
 */
export const getRouteProblems = (input: INumberRoutingInput, selection: IRouteSelection): IRouteProblem[] => {
  if (selection.kind === 'callGroup' && selection.callGroupId === null) return [routeProblem('noGroupSelected')];

  const { callGroupId } = getRouteFieldsToSave(input, selection);
  if (typeof callGroupId !== 'number') return [];

  const group = input.callGroups.find((callGroup) => callGroup.id === callGroupId);
  if (!group) return [routeProblem('notVisible')];

  const aiAgentEntryProblem = getAiAgentEntryProblem(input, group, selection.kind);
  if (aiAgentEntryProblem) return [aiAgentEntryProblem];

  if (selection.kind === 'callGroup' && !hasEnabledEntry(group)) return [routeProblem('noEnabledEntries', { blocksSave: false })];

  return [];
};

/** A number as a list shows it, with its call group (and the group's entries) and property joined. */
export interface IListedNumber extends Pick<ICallTrackingNumbers, 'callGroupId' | 'usesBuildingNumber' | 'forwardingPhoneNumber'> {
  callGroup?: Pick<ICallGroups, 'name' | 'callGroupEntries'>;
  building?: Pick<IBuildings, 'phone'>;
}

/** Where a routed number's calls go when its call group or the agent can't take them. */
export interface INumberFallback {
  phoneNumber: string | null;
  usesPropertyPhone: boolean;
}

/** Where a routed number's calls go, and where they fall back to when the group or agent can't take them. */
export interface IRouteDestination {
  kind: TGroupRouteKind;
  groupName: string | null;
  fallback: INumberFallback;
}

/**
 * Returns a number's fallback: the property phone or the custom forwarding number, whichever its forwarding settings pick.
 * @param number - The number, with its property joined for the property phone.
 * @returns The fallback phone number, null when none is set, and whether it is the property phone.
 */
export const getNumberFallback = (number: Omit<IListedNumber, 'callGroupId' | 'callGroup'>): INumberFallback => {
  const usesPropertyPhone = number.usesBuildingNumber === 1;
  return {
    phoneNumber: (usesPropertyPhone ? number.building?.phone : number.forwardingPhoneNumber) ?? null,
    usesPropertyPhone,
  };
};

/**
 * Describes where a number routed to a call group sends its calls, for lists that summarise numbers.
 * The group is the AI leasing agent when it holds an AI agent entry, as the client's AI Agent group does,
 * so the list needs only the number's own row and not the client's call groups.
 * @param number - The number as the list shows it, with its call group, the group's entries and its property joined.
 * @returns The destination, or null for a number without a call group, whose forwarding settings describe it already.
 */
export const getRouteDestination = (number: IListedNumber): IRouteDestination | null => {
  if (typeof number.callGroupId !== 'number') return null;

  return {
    kind: number.callGroup && hasAiAgentEntry(number.callGroup) ? 'aiAgent' : 'callGroup',
    groupName: number.callGroup?.name ?? null,
    fallback: getNumberFallback(number),
  };
};

/** What the viewer is allowed to see when looking at where a call group is used. */
export interface ICallGroupUsageVisibility {
  /** Whether the user may read call tracking data for the building. */
  canReadBuilding: (buildingId: number) => boolean;
  /** Whether any of the client's properties has the IVR product. */
  clientHasIVRProperty: boolean;
}

/**
 * Returns the IVR routes using a call group that the user may see: those on buildings they can read.
 * Each route keeps only its readable building links, so the property it is shown under is one the user can see.
 * None are shown when the client has no IVR property.
 */
const getVisibleIVRRoutes = (
  routes: ICallGroupsHasInteractiveVoiceResponses[],
  visibility: ICallGroupUsageVisibility,
): ICallGroupsHasInteractiveVoiceResponses[] => {
  if (!visibility.clientHasIVRProperty) return [];

  return routes.flatMap((route) => {
    const ivr = route.interactiveVoiceResponse;
    const readableLinks = (ivr?.buildingsHasInteractiveVoiceResponses ?? [])
      .filter((link) => typeof link.building?.id === 'number' && visibility.canReadBuilding(link.building.id));

    if (!ivr || !readableLinks.length) return [];

    return [{ ...route, interactiveVoiceResponse: { ...ivr, buildingsHasInteractiveVoiceResponses: readableLinks } }];
  });
};

/**
 * What deleting a call group needs from the user first.
 * `blocked`: an IVR route uses the group, so it must be removed from the route first.
 * `confirmWithNumbers`: only call tracking numbers use it; they go back to their forwarding number once it is deleted.
 * `plainConfirm`: nothing uses it.
 */
export type TCallGroupDeleteDecision = 'blocked' | 'confirmWithNumbers' | 'plainConfirm';

/** Where a call group is used, as far as the user may see, and what deleting it takes. */
export interface ICallGroupUsage {
  visibleIVRRoutes: ICallGroupsHasInteractiveVoiceResponses[];
  visibleNumbers: ICallTrackingNumbers[];
  /** Numbers using the group on properties the user can't read. They are not listed, but they count toward the delete decision. */
  hiddenNumberCount: number;
  deleteDecision: TCallGroupDeleteDecision;
}

const getDeleteDecision = (routeCount: number, numberCount: number): TCallGroupDeleteDecision => {
  if (routeCount) return 'blocked';
  if (numberCount) return 'confirmWithNumbers';

  return 'plainConfirm';
};

/**
 * Works out where a call group is used and what deleting it takes.
 * The user sees the IVR routes and numbers on buildings they can read; a number without a property is shown to anyone.
 * Numbers pending release are left out, as the call tracking lists leave them out.
 * The delete decision counts every route and number, including hidden ones, so a partial view never weakens the warning.
 * @param group - The group, with its IVR route links and call tracking numbers joined.
 * @param visibility - What the user may see.
 * @returns The visible routes and numbers, how many numbers are hidden, and what deleting the group needs first.
 */
export const getCallGroupUsage = (
  group: Pick<ICallGroups, 'callGroupsHasInteractiveVoiceResponses' | 'callTrackingNumbers'>,
  visibility: ICallGroupUsageVisibility,
): ICallGroupUsage => {
  const routes = group.callGroupsHasInteractiveVoiceResponses ?? [];
  const numbers = (group.callTrackingNumbers ?? []).filter((number) => number.pendingDelete !== 1);
  const visibleNumbers = numbers.filter((number) => typeof number.buildingId !== 'number' || visibility.canReadBuilding(number.buildingId));

  return {
    visibleIVRRoutes: getVisibleIVRRoutes(routes, visibility),
    visibleNumbers,
    hiddenNumberCount: numbers.length - visibleNumbers.length,
    deleteDecision: getDeleteDecision(routes.length, numbers.length),
  };
};
